import { IUserState, IUserStateCallback } from '../interface/userstate';

interface IUserStatePrv extends IUserState {
    name: string;
    collection: Record<string, any>;
    callbacks: Record<string, Array<IUserStateCallback<any>>>;
}

interface IUserStateSaveStructure {
    name: string;
    collection: Record<string, any>;
}

const StoreSymbol = Symbol.for('user-state');

function _saveUserState(key: string, state: IUserStatePrv) {
    const store: Record<string, IUserStatePrv> = (global as any)[StoreSymbol] ?? {};
    store[key] = state;
    (global as any)[StoreSymbol] = store;
}

function _loadFromStore(name: string): IUserStatePrv | null {
    const store: Record<string, IUserStatePrv> = (global as any)[StoreSymbol] ?? {};
    const val = store[name];
    return val;
}

function _GetValue(state: IUserStatePrv, key: string): any | undefined {
    return state.collection[key];
}

function _SetValue(state: IUserStatePrv, key: string, value: any | undefined) {
    if (value === undefined) {
        delete state.collection[key];
    } else {
        state.collection[key] = value;
    }
    _saveUserState(state.name, state);
}

function _init(name: string, startingData: Record<string, any>): IUserState {
    const val: IUserStatePrv = {
        name,
        collection: startingData,
        callbacks: {},
        getProperty: function <T>(this: IUserStatePrv, key: string) {
            return _GetValue(this, key) as T | undefined;
        },
        getPropertyWithDefault: function <T>(this: IUserStatePrv, key: string, defaultValue: T) {
            return (_GetValue(this, key) as T | undefined) ?? defaultValue;
        },
        setProperty: function (this: IUserStatePrv, key: string, value: any | undefined) {
            _SetValue(this, key, value);
            const cb = this.callbacks[key] ?? [];

            for (const callback of cb) {
                callback(key, value);
            }
        },
        subscribeChanges: function <T>(
            this: IUserStatePrv,
            key: string,
            callback: IUserStateCallback<T>,
        ) {
            const arr = this.callbacks[key] ?? [];
            arr.push(callback);
            this.callbacks[key] = arr;
        },
        unsubscribeChanges: function <T>(
            this: IUserStatePrv,
            key: string,
            callback: IUserStateCallback<T>,
        ) {
            const arr = this.callbacks[key] ?? [];
            this.callbacks[key] = arr.filter(cb => cb !== callback);
        },
        flatten: function (this: IUserStatePrv): string {
            const data: IUserStateSaveStructure = {
                name: this.name,
                collection: this.collection,
            };
            return JSON.stringify(data);
        },
    };

    return val as IUserState;
}

function load(name: string): IUserState {
    return _loadFromStore(name) ?? _init(name, {});
}

function expand(data: string): IUserState {
    const dataObj: IUserStateSaveStructure = data.length > 2 ? JSON.parse(data) : {};
    return _init(dataObj.name, dataObj.collection);
}

const module = {
    load,
    expand,
};

export default module;
